// @flow

import Symbol from 'core-js/es6/symbol';

import Q from 'q';
import _ from 'lodash';

import ko from 'rgs-knockout';
import kv from 'knockout.validation';

import $ from 'jquery';

import validationDecor from 'validation-decoration';

import SinglePromiseQueue from 'single-promise-queue';


class ValidationBindingState {
	_message: null | string | Array<string>;
	_observable: any;

	constructor ({ message, observable }) {
		this._message = message;
		this._observable = observable;
	}

	get message () {
		return this._message;
	}

	get observable () {
		return this._observable;
	}

	eq (other) {
		return _.isEqual(this._message, other._message)
			&& this._observable === other._observable
		;
	}
}


const defaultMessageAnimator = new validationDecor.MessageAnimator();
const defaultMessageDecorator = new validationDecor.MessageDecorator();
const defaultControlHighlighter = new validationDecor.ControlHighlighter();

const _validationContext = Symbol('_validationContext ');

function closestValidationContext (bindingContext) {
	if (!bindingContext) {
		return undefined;
	}

	const ctx = bindingContext[_validationContext];

	if (ctx) {
		return ctx;
	}

	return closestValidationContext(bindingContext.$parentContext);
}

function getFromClosestValidationContext (bindingContext, path) {
	const ctx = closestValidationContext(bindingContext);

	return ctx && _.get(ctx(), path);
}

ko.bindingHandlers.validationContext = {
	init (...args) {
		const [ /*element*/, valueAccessor, /*allBindings*/, /*viewModel*/, bindingContext ] = args;
		bindingContext[_validationContext] = valueAccessor;
	},
};

ko.bindingHandlers._validationGroupMessage = {
	after: ['validationContext'],
	init: function (...args) {
		const [ element, valueAccessor, /*allBindings*/, /*viewModel*/, bindingContext ] = args;

		const defaults = {
			// validation state descriptor : ValidationBindingState
			state: null,

			// observable that tells if we should display the message
			display: true,

			// object that creates an error element with given message
			decorator: defaultMessageDecorator,

			// object that knows how to animate the error element
			animator: defaultMessageAnimator,

			messageAnimatorLabel: undefined,
			messageAnimatorInsertBefore: undefined,

			logger: undefined,
		};

		const params = _.extend({}, defaults, valueAccessor());

		if (!params.logger) {
			params.logger = getFromClosestValidationContext(bindingContext, 'logger');
		}

		if (!params.logger) {
			params.logger = { push: _.noop };
		}

		function getLabel () {
			return _.get($(params.messageAnimatorLabel), ['0', 'innerText']);
		}

		// Watch the `message` and animate all the things
		const animationsQueue = new SinglePromiseQueue();
		let lastInsertedMessageElement = null;
		let lastReachedState: ValidationBindingState = new ValidationBindingState({
			observable: null,
			message: null,
		});

		ko.computed(function () {
			const state = ko.unwrap(valueAccessor().display) && ko.unwrap(valueAccessor().state);

			let { message: msg } = state;

			function log (extra: ?Object) {
				params.logger.push(_.extend({
					observable: state.observable,
					message: msg,
					element: {
						visible: $(element).is(':visible'),
						label: getLabel(),
					},
				}, extra));
			}

			if (msg) {
				if (_.isArray(msg) && msg.length) {
					// got an array of messages to select from
					msg = params.decorator.selectMessageThatFitsContainer({
						messages: msg,
						container: element,
					});
				}

				const messageElement = params.decorator.createElementWithMessage({
					message: msg,
				});

				animationsQueue.push(function () {
					if (state.eq(lastReachedState)) {
						return Q();
					}

					if (lastInsertedMessageElement) {
						log({ event: 'replaceMessage' });

						return params.animator.replaceError({
							currentError: lastInsertedMessageElement,
							newError: messageElement,
						}).done(function () {
							lastInsertedMessageElement = messageElement;
							lastReachedState = state;
						});
					}

					log({ event: 'insertMessage' });

					return params.animator.insertError({
						error: messageElement,
						container: element,
						label: params.messageAnimatorLabel,
						insertBefore: params.messageAnimatorInsertBefore,
					}).done(function () {
						lastInsertedMessageElement = messageElement;
						lastReachedState = state;
					});
				});

			} else {

				animationsQueue.push(function () {
					if (lastInsertedMessageElement) {
						log({ event: 'removeMessage' });

						return params.animator.removeError({
							container: element,
							error: lastInsertedMessageElement,
							label: params.messageAnimatorLabel,
						}).done(function () {
							lastInsertedMessageElement = null;
							lastReachedState = state;
						});
					}

					return Q();
				});
			}
		}, null, {
			disposeWhenNodeIsRemoved: element,
		}).extend({
			rateLimit: { timeout: 100, method: "notifyWhenChangesStop" },
		});
	},
};

ko.bindingHandlers.validationGroupMessageForObservables = {
	after: ko.bindingHandlers._validationGroupMessage.after,
	init: function (element, valueAccessor, allBindings, viewModel, bindingContext) {
		const defaults = {
			// list of validated observables we display message for
			validated: [],

			// observable that tells if we should display a message
			display: true,

			// object that creates an error element with given message
			decorator: defaultMessageDecorator,

			// object that knows how to animate the error element
			animator: defaultMessageAnimator,
		};

		const params = _.extend({}, defaults, valueAccessor());

		function getLabel () {
			return _.get($(params.messageAnimatorLabel), ['0', 'innerText']);
		}

		function observableValidationNameFallback (observable) {
			// HACK: fill `validationLoggingName` with label from DOM
			if (!ko.isObservable(observable)) {
				return;
			}

			if (observable.validationLoggingName) {
				return;
			}

			observable.extend({
				validationLoggingName: getLabel(),
			});
		}

		const state = ko.pureComputed(function () {
			let validated = valueAccessor().validated;

			if (ko.isObservable(validated) && !validated.error) { // if it's not a validated observable
				validated = ko.unwrap(validated);
			}

			const firstInvalid = kv.firstInvalid(validated);


			if (!firstInvalid) {
				return new ValidationBindingState({
					message: null,
					observable: null,
				});
			}

			observableValidationNameFallback(firstInvalid);

			return new ValidationBindingState({
				message: firstInvalid.error(),
				observable: firstInvalid,
			});
		}, null, {
			disposeWhenNodeIsRemoved: element,
		});

		ko.applyBindingAccessorsToNode(element, {
			_validationGroupMessage: function () {
				return _.extend({}, params, {
					state: state,
					display: _.extend({}, defaults, valueAccessor()).display,
				});
			},
		}, bindingContext);
	},
};

ko.bindingHandlers.validationSetModifiedOn = {
	init: function (element, valueAccessor) {
		let evt = ko.unwrap(valueAccessor());

		if (!_.isArray(evt)) {
			evt = [ evt ];
		}

		$(element).on(evt.join(' '), function () {
			// TODO: this could be simplified using validatable observables
			// passed here from validationControlHighlightForObservables
			const accessors = kv.findAllValidatedObservablesAccessors(element);
			let validatedObservables = [];
			_.each(accessors, function (acc) {
				validatedObservables = validatedObservables.concat(acc());
			});
			_.each(validatedObservables, function (obs) {
				if (kv.utils.isValidatable(obs)) {
					obs.isModified(true);
				}
			});
		});
	},
};

ko.bindingHandlers._validationControlHighlight = {
	init: function (element, valueAccessor, allBindings, viewModel, bindingContext) {
		const defaults = {
			// observable that tells if the element has a validation error or it's valid
			isInvalid: false,

			// observable that tells if we should highlight the element (if the element is modified by user)
			display: true,

			// object that knows how to highlight/unhighlight the element
			highlighter: defaultControlHighlighter,
		};

		const animationsQueue = new SinglePromiseQueue();
		ko.computed(function () {
			const params = _.extend({}, defaults, valueAccessor());

			const invalid = ko.unwrap(params.isInvalid);
			const display = ko.unwrap(params.display);

			const method = display
				? (invalid ? 'highlight' : 'highlightSuccess')
				: 'unhighlight';

			animationsQueue.push(function () {
				return params.highlighter[method]({
					control: element,
				});
			});
		}, null, {
			disposeWhenNodeIsRemoved: element,
		});

		ko.applyBindingsToNode(element, {
			validationSetModifiedOn: 'blur',
		}, bindingContext);
	},
};

ko.bindingHandlers.validationControlHighlightForObservables = {
	init: function (element, valueAccessor, allBindings, viewModel, bindingContext) {
		const defaults = {
			// list of validated observables
			validated: [],

			// observable that tells if we should highlight
			display: true,

			// object that knows how to highlight/unhighlight the element
			highlighter: defaultControlHighlighter,
		};

		const params = _.extend({}, defaults, valueAccessor());

		const validatedComputed = ko.pureComputed(function () {
			let validated = valueAccessor().validated;
			if (ko.isObservable(validated) && !validated.error) {
				validated = ko.unwrap(validated);
			}

			if (!validated) {
				return [];
			}
			if (!_.isArray(validated)) {
				validated = [validated];
			}

			return _.filter(validated);
		}, null, {
			disposeWhenNodeIsRemoved: element,
		});

		const hasInvalid = ko.pureComputed(function () {
			const validated = validatedComputed();

			return _.some(validated, obs => obs.isValid && !obs.isValid());
		}, null, {
			disposeWhenNodeIsRemoved: element,
		});

		const hasModified = ko.pureComputed(function () {
			if (!ko.unwrap(params.display)) {
				return false;
			}

			const validated = validatedComputed();

			return _.reduce(validated, function (hasModified, obs) {
				return hasModified || (obs.isModified && obs.isModified());
			}, false);
		}, null, {
			disposeWhenNodeIsRemoved: element,
		});

		ko.applyBindingAccessorsToNode(element, {
			_validationControlHighlight: function () {
				return {
					isInvalid: hasInvalid,
					display: hasModified,
					highlighter: params.highlighter,
				};
			},
		}, bindingContext);
	},
};
